import { Box, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryIcon from '@mui/icons-material/History';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { DOC_CARD_STATE, docCardState, isConfirmedDoc } from '../../../lib/packingListWire';
import DocStatusChip from './DocStatusChip';

/** 진행 중 작업까지 본 카드 상태의 칩. 파싱 중·파싱 실패·파싱 포기는 문서 status만으로는 안 보인다. */
const CARD_STATE_CHIP = {
    [DOC_CARD_STATE.PARSING]: { status: 'parsing', spinner: true, title: 'The parser is reading the original. The card opens the progress view; review starts when it finishes.' },
    [DOC_CARD_STATE.PARSE_FAILED]: { status: 'draft', note: 'Parse failed', title: 'The original could not be parsed. Enter the lines manually or retry parsing from the review screen.' },
    [DOC_CARD_STATE.FAILED]: { status: 'failed', title: 'Parsing gave up after several attempts. Delete this document and upload the file again.' },
};

/** 카드 둘째 줄 파일명의 최대 글자 수. 넘으면 가운데를 줄인다. */
const FILENAME_MAX = 34;

/**
 * 긴 파일명을 가운데에서 줄인다. 같은 문서의 여러 판은 보통 뒤쪽(_v2·_revised·확장자)에서 갈리므로 끝을 남긴다.
 * 예: PackingList_ShanghaiFactory_2026_v2_revised.pdf → PackingList_Shang…_v2_revised.pdf
 */
export const middleTruncate = (name, max = FILENAME_MAX) => {
    const text = String(name ?? '');
    if (text.length <= max) return text;
    const tail = Math.ceil((max - 1) / 2);
    return `${text.slice(0, max - 1 - tail)}…${text.slice(-tail)}`;
};

/**
 * 카드 둘째 줄의 출처. 원본 파일이 있으면 파일명, 수기 문서는 null이다.
 * 서버가 파일명을 아직 보내지 않는 동안에도 파일 문서를 수기로 잘못 표시하지 않도록, 확정 원본·진행 중 파일 업로드가 있으면 파일로 본다.
 */
export const documentSource = (doc) => {
    if (doc?.original_filename) return doc.original_filename;
    if (doc?.il_file_id_current != null || doc?.open_upload?.input_source === 'file') return 'Uploaded file';
    return null;
};

/**
 * HBL에 붙은 PL 문서 카드. Draft 카드는 업로드 다이얼로그에서 검수를 이어가고, Confirmed 카드는 그 PL의 아이템만 거른다. 다시 누르면 전체로 돌아간다.
 * onDelete가 있으면 Draft 카드 우상단(Edit 오른쪽)에 빨간 휴지통이 붙고, deleteConfirmed면 Confirmed 카드에도 붙는다. onDownload가 있으면 확정 원본 파일이 있는 카드(il_file_id_current)에 다운로드 버튼이 붙는다.
 * onEdit이 있으면 Confirmed 카드에, onEditDraft가 있으면 Draft 카드에 Edit 버튼이 붙는다. onHistory가 있으면 모든 카드에 History(시계) 버튼이 붙는다.
 * 제목은 문서 번호(invoice_no)이고, 아직 없으면 Untitled다. 그 아래 줄은 출처로, 원본 파일명(가운데 줄임) 또는 Manual entry다.
 * 파일명은 난수이거나 길 수 있어 대표 이름으로 쓰지 않는다.
 * 파싱 중이면 Parsing, 파싱이 실패한 초안이면 Draft + Parse failed, 파싱을 끝내 포기한 문서면 Failed 칩을 단다.
 * 검수 중인 파일 초안의 원본은 검수 화면의 뷰어에서 내려받는다.
 */
export default function PLDocumentList({ docs, selectedId, onSelect, onDelete, deleteConfirmed = false, onDownload, onEdit, onEditDraft, onHistory }) {
    if (docs.length === 0) return null;
    return (
        <Box sx={{ display: 'flex', gap: '13px', flexWrap: 'wrap', alignItems: 'stretch' }}>
            {docs.map((d) => {
                const confirmed = isConfirmedDoc(d);
                const cardState = docCardState(d);
                const stateChip = CARD_STATE_CHIP[cardState];
                const editCard = confirmed ? onEdit : onEditDraft;
                const deletable = onDelete && (deleteConfirmed || !confirmed);
                return (
                    <Box
                        key={d.id}
                        onClick={() => onSelect(selectedId === d.id ? null : d.id)}
                        sx={{ position: 'relative', minWidth: 220, maxWidth: 280, p: 1, border: '1px solid', borderColor: selectedId === d.id ? '#00647e' : '#e6ebee', borderRadius: '8px', bgcolor: 'white', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 0.5 }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title={stateChip?.title ?? ''} arrow disableHoverListener={!stateChip}>
                                    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                                        <DocStatusChip status={stateChip?.status ?? d.status} sx={stateChip?.spinner ? { pl: '14px' } : undefined} />
                                        {stateChip?.spinner && <CircularProgress size={10} sx={{ position: 'absolute', left: 6, color: 'white' }} />}
                                    </Box>
                                </Tooltip>
                                {stateChip?.note && <Chip size="small" variant="outlined" color="warning" label={stateChip.note} sx={{ fontSize: 10, height: 18 }} />}
                            </Box>
                            {(editCard || deletable || onHistory) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                    {onHistory && (
                                        <Tooltip title="History">
                                            <IconButton
                                                size="small"
                                                aria-label="History"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onHistory(d);
                                                }}
                                                sx={{ p: 0.25, color: '#c7c7c7', '&:hover': { color: 'text.secondary' } }}
                                            >
                                                <HistoryIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {editCard && (
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                aria-label="Edit"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    editCard(d);
                                                }}
                                                sx={{ p: 0.25, color: '#c7c7c7', '&:hover': { color: 'text.secondary' } }}
                                            >
                                                <EditOutlinedIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {deletable && (
                                        <Tooltip title={confirmed ? 'Delete this packing list' : 'Delete this draft'}>
                                            <IconButton
                                                size="small"
                                                aria-label="Delete document"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDelete(d.id);
                                                }}
                                                sx={{ p: 0.25, color: '#c7c7c7', '&:hover': { color: 'error.main' } }}
                                            >
                                                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, minWidth: 0 }}>
                            {d.invoice_no ? (
                                <Typography title={d.invoice_no} sx={{ ml: '5px', fontSize: 12, fontWeight: 600, color: '#01354d', minWidth: 0 }} noWrap>{d.invoice_no}</Typography>
                            ) : (
                                <Typography sx={{ ml: '5px', fontSize: 12, fontStyle: 'italic', color: 'text.disabled' }}>{cardState === DOC_CARD_STATE.PARSING ? 'Reading the original…' : 'Untitled'}</Typography>
                            )}
                            {onDownload && d.il_file_id_current != null && (
                                <Tooltip title="Download the original file">
                                    <IconButton
                                        size="small"
                                        aria-label="Download original"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDownload(d.id);
                                        }}
                                        sx={{ p: 0.25 }}
                                    >
                                        <DownloadIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                        {documentSource(d) ? (
                            <Box title={documentSource(d)} sx={{ ml: '5px', display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, color: 'text.secondary' }}>
                                <InsertDriveFileOutlinedIcon sx={{ fontSize: 13, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{middleTruncate(documentSource(d))}</Typography>
                            </Box>
                        ) : (
                            <Typography sx={{ ml: '5px', fontSize: 11, color: 'text.secondary' }}>Manual entry</Typography>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
